import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  FleetEscalation,
  FleetJournal,
  FleetOverviewRun,
  ackEscalation,
  approveHalt,
  fetchJournalTail,
} from '../../services/fleetHudApi';

type JournalState = 'loading' | 'error' | FleetJournal;

interface ActionRequiredProps {
  escalations: FleetEscalation[];
  latestRun: FleetOverviewRun | null;
  onApproved?: () => void;
  /** Called after a successful escalation ACK — the parent refetches so the
      acked report leaves the list + open count. */
  onAcked?: () => void;
}

export default function ActionRequired({ escalations, latestRun, onApproved, onAcked }: ActionRequiredProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [journals, setJournals] = useState<Record<string, JournalState>>({});
  const [approveInFlight, setApproveInFlight] = useState<Record<string, boolean>>({});
  const [approvedRuns, setApprovedRuns] = useState<Record<string, boolean>>({});
  const [ackInFlight, setAckInFlight] = useState<Record<string, boolean>>({});

  // Escalation ACK (2026-08-14): double-click-safe, same pattern as approve.
  // The report stays in the record — it only leaves the attention surfaces.
  const handleAck = async (escId: string) => {
    if (ackInFlight[escId]) return;
    setAckInFlight((prev) => ({ ...prev, [escId]: true }));
    try {
      await ackEscalation(escId);
      onAcked?.();
    } catch {
      /* ack failed — row stays; next poll re-renders the truth */
    } finally {
      setAckInFlight((prev) => ({ ...prev, [escId]: false }));
    }
  };

  // Halted runs: the latest run when it sits in human_escalated, plus any run an
  // escalation flagged as requiring a human (deduped by run_id).
  const haltedRunIds: string[] = [];
  if (latestRun && latestRun.status === 'human_escalated' && latestRun.run_id) {
    haltedRunIds.push(latestRun.run_id);
  }
  for (const esc of escalations) {
    if (esc.human_escalation_required && esc.run_id && !haltedRunIds.includes(esc.run_id)) {
      haltedRunIds.push(esc.run_id);
    }
  }

  const toggleRow = (key: string, runId?: string) => {
    const next = !expanded[key];
    setExpanded((prev) => ({ ...prev, [key]: next }));
    // Lazy journal fetch: exactly one request per run, only ever on expand.
    if (next && runId && !journals[runId]) {
      setJournals((prev) => ({ ...prev, [runId]: 'loading' }));
      fetchJournalTail(runId)
        .then((j) => setJournals((prev) => ({ ...prev, [runId]: j })))
        .catch(() => setJournals((prev) => ({ ...prev, [runId]: 'error' })));
    }
  };

  const handleApprove = async (runId: string) => {
    if (approveInFlight[runId] || approvedRuns[runId]) return;
    if (!window.confirm(`Approve halt for ${runId}? The run can then be cleanly re-fired.`)) return;
    // Set the in-flight flag BEFORE the await so a rapid second click is a no-op.
    setApproveInFlight((prev) => ({ ...prev, [runId]: true }));
    try {
      await approveHalt(runId);
      setApprovedRuns((prev) => ({ ...prev, [runId]: true }));
      onApproved?.();
    } catch {
      // api util already surfaced the error toast — leave the row actionable.
    } finally {
      setApproveInFlight((prev) => ({ ...prev, [runId]: false }));
    }
  };

  const renderJournal = (runId?: string) => {
    if (!runId) return null;
    const j = journals[runId];
    if (!j || j === 'loading') {
      return <p className="px-3 py-2 text-xs text-text-muted font-mono">Loading journal…</p>;
    }
    if (j === 'error') {
      return <p className="px-3 py-2 text-xs text-red-500 font-mono">Journal unavailable.</p>;
    }
    if (!j.entries || j.entries.length === 0) {
      return <p className="px-3 py-2 text-xs text-text-muted font-mono">No journal entries.</p>;
    }
    return (
      <pre className="max-h-64 overflow-auto px-3 py-2 text-xs leading-relaxed font-mono text-text-secondary whitespace-pre-wrap">
        {j.entries.map((e) => `[${e.ts ?? ''}] ${(e.level ?? 'info').toUpperCase()} ${e.message ?? ''}`).join('\n')}
      </pre>
    );
  };

  const empty = haltedRunIds.length === 0 && escalations.length === 0;

  return (
    <section className="rounded-lg border border-hairline bg-surface">
      <header className="border-b border-hairline px-5 py-3">
        <h2 className="text-sm font-semibold tracking-wide text-text-primary">
          ACTION REQUIRED
          <span className="ml-2 font-mono text-xs text-text-muted">
            {haltedRunIds.length + escalations.length}
          </span>
        </h2>
      </header>

      {empty ? (
        <p className="px-5 py-4 text-sm text-text-muted">No open escalations, no halted runs.</p>
      ) : (
        <ul className="divide-y divide-hairline">
          {haltedRunIds.map((runId) => {
            const key = `halt-${runId}`;
            const isOpen = !!expanded[key];
            const inFlight = !!approveInFlight[runId];
            const approved = !!approvedRuns[runId];
            return (
              <li key={key}>
                <div className="flex flex-wrap items-center gap-3 px-5 py-3">
                  <button
                    type="button"
                    onClick={() => toggleRow(key, runId)}
                    className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-left"
                    aria-expanded={isOpen}
                  >
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4 text-text-muted" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-text-muted" />
                    )}
                    <span className="font-mono text-sm text-text-primary">{runId}</span>
                    <span className="text-xs uppercase tracking-wide text-brand">
                      halted — awaiting approval
                    </span>
                  </button>
                  <button
                    type="button"
                    disabled={inFlight || approved}
                    onClick={() => handleApprove(runId)}
                    className={`rounded border px-3 py-1 text-xs font-semibold tracking-wide ${
                      approved
                        ? 'border-hairline text-text-muted cursor-default'
                        : inFlight
                          ? 'border-hairline text-text-muted cursor-wait'
                          : 'border-brand text-brand hover:bg-brand-tint'
                    }`}
                  >
                    {approved ? 'APPROVED' : inFlight ? 'APPROVING…' : 'APPROVE'}
                  </button>
                </div>
                {isOpen && <div className="border-t border-hairline bg-elevated">{renderJournal(runId)}</div>}
              </li>
            );
          })}

          {escalations.map((esc, idx) => {
            const key = `esc-${esc.id ?? idx}`;
            const isOpen = !!expanded[key];
            const reason =
              esc.root_cause_hypothesis && esc.root_cause_hypothesis !== 'Unknown'
                ? esc.root_cause_hypothesis
                : esc.error_messages && esc.error_messages.length > 0
                  ? String(esc.error_messages[0])
                  : esc.reason
                    ? // pr_only records carry `reason` (+ pr_url) instead of
                      // error_messages — before this fallback every one of
                      // them rendered the same anonymous placeholder row.
                      `${String(esc.reason)}${esc.pr_number ? ` — PR #${esc.pr_number}` : ''}`
                    : 'Escalation reported — expand for journal.';
            const shortRun = esc.run_id ? String(esc.run_id).slice(-6) : null;
            return (
              <li key={key}>
                <button
                  type="button"
                  onClick={() => toggleRow(key, esc.run_id)}
                  className="flex w-full items-center gap-2 px-5 py-3 text-left"
                  aria-expanded={isOpen}
                >
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4 shrink-0 text-text-muted" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-text-muted" />
                  )}
                  <span className="font-mono text-xs text-text-muted shrink-0">
                    {shortRun ? `…${shortRun}` : (esc.item_id ?? '—')}
                  </span>
                  <span className="text-xs uppercase tracking-wide text-text-muted shrink-0">
                    {esc.severity ?? 'medium'}
                  </span>
                  {/* min-w-0 flex-1: without it long reason text refuses to
                      shrink and shoves the ack chip past the rail's right
                      edge — 'no idea how to clear them' (owner, 2026-08-14). */}
                  <span className="min-w-0 flex-1 truncate text-sm text-text-primary">{reason}</span>
                  {esc.id && (
                    <span
                      role="button"
                      tabIndex={0}
                      data-testid={`ack-${esc.id}`}
                      title="Acknowledge — leaves the attention list; the report stays in the record"
                      className="ml-auto shrink-0 rounded-sm border border-hairline px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-text-muted hover:border-brand hover:text-brand"
                      onClick={(e) => {
                        // Never let the ack gesture expand the row.
                        e.stopPropagation();
                        void handleAck(esc.id!);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          void handleAck(esc.id!);
                        }
                      }}
                    >
                      {ackInFlight[esc.id] ? '…' : 'ack'}
                    </span>
                  )}
                </button>
                {isOpen && (
                  <div className="border-t border-hairline bg-elevated">
                    {esc.recommended_manual_steps ? (
                      <p className="px-3 pt-2 text-xs text-text-secondary">
                        {esc.recommended_manual_steps}
                      </p>
                    ) : null}
                    {esc.pr_url ? (
                      <p className="px-3 pt-2 text-xs">
                        <a
                          href={String(esc.pr_url)}
                          target="_blank"
                          rel="noreferrer"
                          className="font-mono text-brand hover:underline"
                        >
                          {esc.pr_number ? `PR #${esc.pr_number}` : 'Open PR'} ↗
                        </a>
                      </p>
                    ) : null}
                    {renderJournal(esc.run_id)}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
